<script lang="ts">
export default {
  name: 'PolicySubitemsPanel',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Lock, Plus, Trash2, Undo2 } from '@lucide/vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { addCommas, removeCommas } from '@/utilities/Number';
import type { SubitemKind, SubitemRow } from '@/composables/usePolicyEditPending';
import type { SubitemTypeRef } from '@/types/PolicySublimit';

const props = defineProps<{
  kind: SubitemKind;
  rows: SubitemRow[];
  availableTypes: SubitemTypeRef[];
  isDisabled: boolean;
  loading: boolean;
  // Set while a coverage type change is staged: the types on offer are still the old coverage's.
  addBlockedReason?: string;
}>();

const emit = defineEmits<{
  (e: 'add', type: SubitemTypeRef): void;
  (e: 'update', payload: { rowKey: string; value: string; basis?: string }): void;
  (e: 'remove', rowKey: string): void;
  (e: 'undo', rowKey: string): void;
}>();

const selectedTypeId = ref<string>('');

const isSublimit = computed(() => props.kind === 'sublimit');
const noun = computed(() => (isSublimit.value ? 'sublimit' : 'subretention'));
const valueLabel = computed(() => (isSublimit.value ? 'Limit' : 'Retention'));

const typeOptions = computed(() => props.availableTypes.map((type) => ({ label: type.name, value: type.id.toString() })));

function addSelectedType(): void {
  const type = props.availableTypes.find((candidate) => candidate.id.toString() === selectedTypeId.value);
  if (!type) return;
  emit('add', type);
  selectedTypeId.value = '';
}

function onValueInput(row: SubitemRow, raw: string): void {
  emit('update', { rowKey: row.rowKey, value: removeCommas(raw)?.toString() ?? '' });
}

function onBasisInput(row: SubitemRow, basis: string): void {
  emit('update', { rowKey: row.rowKey, value: row.value, basis });
}

const displayValue = (row: SubitemRow): string => (row.value === '' ? '' : addCommas(row.value));

// A user-created item has no AI/Epic original behind it — worth calling out, since removing one
// is the only case where nothing remains to fall back to.
const isUserCreated = (row: SubitemRow): boolean => row.status === 'added' || row.originalValue === undefined;
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-if="loading"
      class="flex h-32 items-center justify-center">
      <span class="loading loading-md loading-spinner"></span>
    </div>

    <template v-else>
      <div
        v-if="rows.length === 0"
        class="rounded border border-base-300 p-6 text-center text-sm text-slate-medium">
        No {{ noun }}s on this line yet.
      </div>

      <table
        v-else
        class="w-full table-fixed">
        <thead>
          <tr class="border-b border-base-300 text-left text-xs tracking-wide text-slate-medium uppercase">
            <th class="w-1/2 py-2 font-semibold">{{ isSublimit ? 'Sublimit' : 'Subretention' }}</th>
            <th class="py-2 font-semibold">{{ valueLabel }}</th>
            <th
              v-if="!isSublimit"
              class="py-2 font-semibold">
              Basis
            </th>
            <th class="w-14 py-2"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.rowKey"
            class="border-b border-base-200 text-sm last:border-b-0"
            :class="{ 'opacity-50': row.status === 'deleted' }">
            <td class="py-2 pr-3 text-slate-dark">
              <span :class="{ 'line-through': row.status === 'deleted' }">{{ row.name }}</span>
              <span
                v-if="row.status === 'added'"
                class="ml-2 badge border-0 bg-success/10 badge-xs text-success"
                >Added</span
              >
              <span
                v-else-if="row.status === 'updated'"
                class="ml-2 badge border-0 bg-warning/10 badge-xs text-warning"
                >Edited</span
              >
              <span
                v-else-if="row.status === 'deleted'"
                class="ml-2 badge border-0 bg-error/10 badge-xs text-error"
                >Removed</span
              >
              <span
                v-else-if="isUserCreated(row)"
                class="ml-2 badge badge-ghost badge-xs text-slate-medium"
                >User added</span
              >
            </td>
            <td class="py-2 pr-3">
              <input
                type="text"
                class="input input-sm w-full"
                :class="{ 'border-success': row.status === 'added' || row.status === 'updated' }"
                :value="displayValue(row)"
                :disabled="isDisabled || row.status === 'deleted'"
                :data-testid="`${kind}-value-${row.rowKey}`"
                @input="onValueInput(row, ($event.target as HTMLInputElement).value)" />
            </td>
            <td
              v-if="!isSublimit"
              class="py-2 pr-3">
              <input
                type="text"
                class="input input-sm w-full"
                :value="row.basis"
                :disabled="isDisabled || row.status === 'deleted'"
                placeholder="e.g. Per Claim"
                @input="onBasisInput(row, ($event.target as HTMLInputElement).value)" />
            </td>
            <td class="py-2 text-right">
              <span
                v-if="row.lockedByCoverageChange"
                class="inline-flex px-3 text-slate-medium"
                title="Removed by the coverage type change. Undo that change to restore it."
                :data-testid="`${kind}-locked-${row.rowKey}`">
                <Lock :size="16" />
              </span>
              <button
                v-else-if="row.status === 'deleted'"
                class="btn btn-ghost btn-sm"
                :disabled="isDisabled"
                :title="`Restore ${noun}`"
                @click="emit('undo', row.rowKey)">
                <Undo2 :size="16" />
              </button>
              <button
                v-else
                class="btn text-error btn-ghost btn-sm"
                :disabled="isDisabled"
                :title="`Remove ${noun}`"
                :data-testid="`${kind}-remove-${row.rowKey}`"
                @click="emit('remove', row.rowKey)">
                <Trash2 :size="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="flex items-end gap-2 border-t border-base-300 pt-3">
        <div class="flex-1">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Add {{ noun }}</span>
          </label>
          <AppSelect
            v-model="selectedTypeId"
            :options="typeOptions"
            :disabled="isDisabled || !!addBlockedReason || typeOptions.length === 0"
            :placeholder="addBlockedReason ?? (typeOptions.length === 0 ? `No more ${noun} types for this coverage` : 'Select a type')"
            :data-testid="`${kind}-type-select`"
            class="h-10 w-full rounded-sm" />
        </div>
        <button
          class="btn h-10 min-h-10 btn-sm btn-primary"
          :disabled="isDisabled || !!addBlockedReason || !selectedTypeId"
          :data-testid="`${kind}-add`"
          @click="addSelectedType">
          <Plus :size="14" />
          Add
        </button>
      </div>
    </template>
  </div>
</template>
